import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle, Monitor, Mic, Wifi, Settings, Users, Zap, Camera, HdmiPort, Usb, Shield } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";

const FeaturesAndBenefitsRXT4D = () => {
  const { t } = useLanguage();

  const keyFeatures = [
    {
      icon: <Monitor className="h-8 w-8 text-[#069E50]" />,
      title: "4-inch Desktop Touch Panel",
      description: "720 x 720 (1:1 aspect ratio) display with 160° viewing angle for optimal visibility from any position"
    },
    {
      icon: <Mic className="h-8 w-8 text-[#069E50]" />,
      title: "Dante™/AES67 Audio",
      description: "Beam forming microphone with advanced feedback suppression, auto gain control, and noise cancellation"
    },
    {
      icon: <Settings className="h-8 w-8 text-[#069E50]" />,
      title: "ReAX™ Control System",
      description: "Fully customizable JavaScript-based control platform with unlimited control capabilities, 2D/3D graphics engine, and 4K30 H.264 streaming decoder"
    },
    {
      icon: <Camera className="h-8 w-8 text-[#069E50]" />,
      title: "Camera RTSP Streams",
      description: "Capture, display and control camera RTSP streams for comprehensive video management and monitoring"
    },
    {
      icon: <HdmiPort className="h-8 w-8 text-[#069E50]" />,
      title: "Content Input & Distribution",
      description: "USB-C and HDMI input for seamless content distribution and presentation capabilities"
    },
    {
      icon: <Usb className="h-8 w-8 text-[#069E50]" />,
      title: "USB-C Connectivity & Charging",
      description: "USB-C display output and 100-watt charging capability for comprehensive device connectivity"
    },
    {
      icon: <Wifi className="h-8 w-8 text-[#069E50]" />,
      title: "Wireless Remote Control",
      description: "Internal web server enables control from smartphones, computers, and other remote devices"
    },
    {
      icon: <Users className="h-8 w-8 text-[#069E50]" />,
      title: "Meeting Enhancement",
      description: "Voting, room booking, name placeholder, raise hand function, and assistive hearing capabilities"
    },
    {
      icon: <Zap className="h-8 w-8 text-[#069E50]" />,
      title: "All-in-One Solution",
      description: "Combines touch control, audio processing, video management, and room automation in a single compact device"
    }
  ];

  const hardwareSpecs = [
    {
      title: "Display & Interface",
      items: [
        "4-inch desktop touch panel with 720 x 720 (1:1 aspect ratio) display",
        "160° viewing angle for wide visibility",
        "Compact desktop form factor with adjustable positioning",
        "Proximity sensor and ambient light sensor"
      ]
    },
    {
      title: "Audio & Video System",
      items: [
        "Built-in 4-watt powered speaker",
        "Dante™/AES67 beam forming microphone functionality",
        "Advanced feedback suppression technology",
        "Automatic gain control and noise cancellation",
        "Camera RTSP stream capture and control",
        "4K30 H.264 streaming decoder"
      ]
    },
    {
      title: "Connectivity & Power",
      items: [
        "USB-C input for content distribution",
        "HDMI input for content distribution",
        "USB-C display output for secondary screens",
        "USB-C charging with 100-watt power delivery",
        "Gigabit Ethernet with PoE+ support"
      ]
    }
  ];

  const audioCapabilities = [
    {
      title: "Professional Audio Processing",
      items: [
        "Dante™/AES67 beam forming microphone with advanced hardware",
        "Feedback suppression, auto gain, and noise cancellation",
        "Mix-minus audio processing for clear communication",
        "Assistive hearing and translation talkback support"
      ]
    },
    {
      title: "Audio Network Integration",
      items: [
        "Seamless integration with Dante™/AES67 audio networks",
        "Professional-grade audio routing capabilities",
        "Compatible with existing audio infrastructure",
        "Low-latency audio processing for real-time applications"
      ]
    }
  ];

  const videoCapabilities = [
    {
      title: "Video Stream Management",
      items: [
        "Real-time camera RTSP stream capture and display",
        "Multiple camera stream support and switching",
        "Video stream recording and playback capabilities",
        "Advanced video processing and enhancement features"
      ]
    },
    {
      title: "Content Distribution",
      items: [
        "USB-C and HDMI input for versatile content sources",
        "USB-C display output for extended screen capabilities",
        "4K30 H.264 streaming for high-quality video distribution",
        "Seamless content switching and routing"
      ]
    }
  ];

  const controlSystem = [
    {
      title: "ReAX™ JavaScript Platform",
      items: [
        "Powerful JavaScript-based control system with unlimited customization",
        "No limits to control capabilities - fully programmable platform",
        "2D/3D graphics engine for complex visual interfaces",
        "4K30 H.264 streaming decoder for video content",
        "Free Core Studio tool for programming and interface creation"
      ]
    },
    {
      title: "Remote Access & Control",
      items: [
        "Internal web server for remote access capabilities",
        "Wireless remote web control via smartphones and computers",
        "Fully customizable interface and functionality",
        "Real-time system monitoring and diagnostics"
      ]
    }
  ];

  const applications = [
    {
      title: "Meeting & Conference Rooms",
      items: [
        "Full conference room automation and control",
        "Video conferencing with camera stream management",
        "Content sharing from multiple input sources",
        "Voting system functionality for decision making",
        "Room booking capabilities with status display",
        "Name placeholder display for participants"
      ]
    },
    {
      title: "Educational & Corporate",
      items: [
        "Educational facility integration and control",
        "Live streaming and recording capabilities",
        "Multi-source content presentation",
        "Hospitality system management",
        "Digital signage applications",
        "Raise hand function for interactive meetings"
      ]
    }
  ];

  const benefits = [
    "All-in-one solution with video, audio, and power in one device",
    "Professional-grade audio and video processing in compact desktop format",
    "100-watt USB-C charging eliminates need for separate power adapters",
    "Camera RTSP stream management for comprehensive video control",
    "Multiple input sources (USB-C, HDMI) for versatile content distribution",
    "USB-C display output enables dual-screen configurations",
    "Seamless integration with existing Dante™/AES67 networks",
    "Enhanced meeting functionality with voting and booking features",
    "Free programming tools reduce deployment costs"
  ];

  const handleViewProduct = () => {
    // Scroll to top when navigating
    window.scrollTo(0, 0);
  };

  return (
    <div className="container mx-auto px-4 py-16">
      <div className="mb-8">
        <Link
          to="/group/meeting-room"
          className="mb-8 inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Meeting Room / ReAX Room Solutions
        </Link>
        <h1 className="mb-4 text-4xl font-bold tracking-tight">
          ReAX Room - RXT-4D, RXT-4DV Desktop Control Panels - Features & Benefits
        </h1>
      </div>

      {/* Key Features Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8">Key Features</h2>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {keyFeatures.map((feature, index) => (
            <div key={index} className="bg-card rounded-lg p-6 border shadow-sm">
              <div className="mb-4">{feature.icon}</div>
              <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
              <p className="text-muted-foreground">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* VPX AVOIP Compatibility Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8">VPX AVOIP Compatibility</h2>
        <div className="bg-gradient-to-r from-[#069E50]/10 to-[#069E50]/5 rounded-lg p-8 border border-[#069E50]/20">
          <div className="flex items-start gap-4">
            <CheckCircle className="h-8 w-8 text-[#069E50] mt-1 flex-shrink-0" />
            <div>
              <h3 className="text-xl font-semibold mb-3 text-[#069E50]">Fully Compatible with VPX AVOIP Range</h3>
              <p className="text-muted-foreground text-lg leading-relaxed">
                The RXT-4D and RXT-4DV desktop control panels are designed to seamlessly integrate with Aurora's entire VPX AVOIP product range, providing comprehensive AV over IP control and management capabilities. This compatibility ensures unified system operation and simplified installation across your complete Aurora ecosystem.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Meeting Security Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8">Enhanced Meeting Security</h2>
        <div className="bg-gradient-to-r from-[#069E50]/10 to-[#069E50]/5 rounded-lg p-8 border border-[#069E50]/20">
          <div className="flex items-start gap-4">
            <Shield className="h-8 w-8 text-[#069E50] mt-1 flex-shrink-0" />
            <div>
              <h3 className="text-xl font-semibold mb-3 text-[#069E50]">Superior Audio Security with Desktop Speakers</h3>
              <p className="text-muted-foreground text-lg leading-relaxed">
                The internal speaker positioned within a few feet of meeting attendees enables significantly lower volume levels compared to traditional in-ceiling speaker solutions. This proximity-based design provides enhanced meeting room security by reducing audio spillover into adjacent spaces, ensuring confidential discussions remain private while maintaining crystal-clear audio quality for all participants.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Hardware Specifications Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8">Hardware Specifications</h2>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {hardwareSpecs.map((spec, index) => (
            <div key={index} className="bg-card rounded-lg p-6 border shadow-sm">
              <h3 className="text-lg font-semibold mb-3 text-[#069E50]">{spec.title}</h3>
              <ul className="space-y-2">
                {spec.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#069E50] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Audio Capabilities Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8">Audio Capabilities</h2>
        <div className="grid gap-6 md:grid-cols-2">
          {audioCapabilities.map((capability, index) => (
            <div key={index} className="bg-card rounded-lg p-6 border shadow-sm">
              <h3 className="text-lg font-semibold mb-3 text-[#069E50]">{capability.title}</h3>
              <ul className="space-y-2">
                {capability.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#069E50] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Video Capabilities Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8">Video Capabilities</h2>
        <div className="grid gap-6 md:grid-cols-2">
          {videoCapabilities.map((capability, index) => (
            <div key={index} className="bg-card rounded-lg p-6 border shadow-sm">
              <h3 className="text-lg font-semibold mb-3 text-[#069E50]">{capability.title}</h3>
              <ul className="space-y-2">
                {capability.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#069E50] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Control System Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8">Control System</h2>
        <div className="grid gap-6 md:grid-cols-2">
          {controlSystem.map((system, index) => (
            <div key={index} className="bg-card rounded-lg p-6 border shadow-sm">
              <h3 className="text-lg font-semibold mb-3 text-[#069E50]">{system.title}</h3>
              <ul className="space-y-2">
                {system.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#069E50] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Applications & Features Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8">Applications & Features</h2>
        <div className="grid gap-6 md:grid-cols-2">
          {applications.map((application, index) => (
            <div key={index} className="bg-card rounded-lg p-6 border shadow-sm">
              <h3 className="text-lg font-semibold mb-3 text-[#069E50]">{application.title}</h3>
              <ul className="space-y-2">
                {application.items.map((item, itemIndex) => (
                  <li key={itemIndex} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#069E50] mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Business Benefits Section */}
      <div className="mb-16">
        <h2 className="text-2xl font-bold mb-8">Business Benefits</h2>
        <div className="bg-card rounded-lg p-8 border shadow-sm">
          <div className="grid gap-4 md:grid-cols-2">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-[#069E50] mt-0.5 flex-shrink-0" />
                <span className="text-muted-foreground">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Call to Action */}
      <div className="text-center bg-muted rounded-lg p-8">
        <h2 className="text-2xl font-bold mb-4">Ready to Learn More?</h2>
        <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
          Discover detailed specifications, documentation, and technical details for the ReAX room suite of products.
        </p>
        <Link to="/group/meeting-room" onClick={handleViewProduct}>
          <Button size="lg">
            View Meeting Room / ReAX Room Solutions
          </Button>
        </Link>
      </div>
    </div>
  );
};

export default FeaturesAndBenefitsRXT4D;
